import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdministratorsService, IOcbCUEnvironmentAdministrator, IOcbEnvironmentAdministrator, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';

@Component({
    selector: 'app-environment-admins',
    templateUrl: './environment-admins.component.html',
    styleUrls: ['./environment-admins.component.scss'],
})
export class EnvironmentAdminsComponent implements AfterViewInit {

    @ViewChild('adminsGrid')
    public adminsGrid: DxDataGridComponent;

    @ViewChild('envAdminValidationGroup')
    public envAdminValidationGroup: DxValidationGroupComponent;

    public admins: IOcbEnvironmentAdministrator[] = [];

    public createPopupVisible: boolean = false;
    public editPopupVisible: boolean = false;

    public editAdmin: IOcbEnvironmentAdministrator;
    public newAdmin: IOcbEnvironmentAdministrator;

    private environmentId: string;

    constructor(
        private adminService: AdministratorsService,
        private route: ActivatedRoute,
        private userService: UserService,
        private translateService: TranslateService) {

    }

    public ngAfterViewInit(): void {
        this.route.params.subscribe(_ => {
            this.environmentId = this.route.snapshot.params.id;
            this.loadData();
        });
    }

    /**
     * Set the custom properties of fields in edit mode
     */
    public onEditorPreparing(e): void {
        switch (e.dataField) {
            case 'canAccessLogs':
            case 'canDisableACKAlerts':
            case 'canExecuteTransfers':
            case 'canManageEnvironmentSettings':
            case 'canManageFileTransfers':
            case 'canManageLocalAgents':
            case 'canManageRemotePlatforms':
                // Show switch instead of checkboxes
                e.editorName = 'dxSwitch';
                break;
        }
    }

    /**
     * Set context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.copy'),
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.showEditPopup(ev, e.row.data) },
                        { text: messages[1], icon: 'copy', onItemClick: (ev) => this.showCreatePopup(ev, e.row.data) },
                        { text: messages[2], icon: 'trash', onItemClick: (ev) => this.deleteAdmin(ev, e.row.data) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Edit an administrator properties
     * @param e Handle event
     */
    public onRowDblClick(e) {
        if (e && e.data) {
            this.showEditPopup(e, e.data);
        }
    }

    /**
     * Shows the admininistrator creation popup.
     * @param e Handle event
     * @param copyAdmin Copy properties from this admin.
     */
    public showCreatePopup(e?, copyAdmin?: IOcbEnvironmentAdministrator): void {
        if (e) {
            e.event.stopPropagation();
        }
        let permissions: IOcbCUEnvironmentAdministrator = {};

        if (copyAdmin) {
            permissions = {
                canAccessLogs: copyAdmin.canAccessLogs,
                canDisableACKAlerts: copyAdmin.canDisableACKAlerts,
                canExecuteTransfers: copyAdmin.canExecuteTransfers,
                canManageEnvironmentSettings: copyAdmin.canManageEnvironmentSettings,
                canManageFileTransfers: copyAdmin.canManageFileTransfers,
                canManageLocalAgents: copyAdmin.canManageLocalAgents,
                canManageRemotePlatforms: copyAdmin.canManageRemotePlatforms,
            };
        }
        else {
            permissions = {
                canAccessLogs: false,
                canDisableACKAlerts: false,
                canExecuteTransfers: false,
                canManageEnvironmentSettings: false,
                canManageFileTransfers: false,
                canManageLocalAgents: false,
                canManageRemotePlatforms: false,
            };
        }

        this.newAdmin = {
            user: null,
            ...permissions,
        };

        this.createPopupVisible = true;
    }

    /**
     * Edit an administrator properties
     * @param e Handle event
     * @param admin Edited administrator.
     */
    public showEditPopup(e, admin: IOcbEnvironmentAdministrator): void {
        if (e && e.event) {
            e.event.stopPropagation();
        }
        this.editAdmin = admin;
        this.editPopupVisible = true;
    }

    /**
     * Creates the new administrator
     * @param admin New admin.
     */
    public createAdmin(admin: IOcbEnvironmentAdministrator): void {
        if (this.envAdminValidationGroup.instance.validate().isValid) {
            this.createPopupVisible = false;
            this.showLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => this.adminService.addEnvironmentAdministrator(this.environmentId, admin.user.id, admin)),
                    mergeMap(_ => this.translateService.get('environmentAdmins.msgAddAdminComplete')),
                    finalize(() => this.adminsGrid.instance.endCustomLoading()),
                )
                .subscribe(
                    message => {
                        // Add admin to list
                        this.admins.push(admin);

                        // Notify
                        notify(message, 'success', 3000);
                    },
                    err => this.handleError(err),
                );
        }
    }

    /**
     * Updates an administrator
     * @param admin Updated admin.
     */
    public updateAdmin(admin: IOcbEnvironmentAdministrator): void {
        if (admin) {
            this.editPopupVisible = false;
            this.showLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => this.adminService.updateEnvironmentAdministrator(this.environmentId, admin.user.id, admin)),
                    mergeMap(_ => this.translateService.get('environmentAdmins.msgUpdateAdminComplete')),
                    finalize(() => this.adminsGrid.instance.endCustomLoading()),
                )
                .subscribe(
                    message => notify(message, 'success', 3000),
                    err => this.handleError(err),
                );
        }
    }

    /**
     * Deletes an administrator.
     * @param e Handle event
     * @param admin Deleted admin.
     */
    public deleteAdmin(e, admin: IOcbEnvironmentAdministrator): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.showLoading$('_commons.loading'),
            this.translateService.get('environmentAdmins.msgDeleteAdminConfirm', { name: admin.user.name }),
            this.translateService.get('environmentAdmins.msgDeleteAdminConfirmTitle'),
        ).pipe(
            // Display confirm popup
            mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
            filter(accepted => accepted),
            // Send the DELETE request
            mergeMap(_ => this.adminService.deleteEnvironmentAdministrator(this.environmentId, admin.user.id)),
            // Get the confirmation message
            mergeMap(_ => this.translateService.get('environmentAdmins.msgDeleteAdminComplete')),
            finalize(() => this.adminsGrid.instance.endCustomLoading()),
        ).subscribe(
            message => {
                // Remove deleted admin from array
                this.admins.splice(this.admins.indexOf(admin), 1);

                // Notify the success
                notify(message, 'success', 3000);
            },
            err => this.handleError(err),
        );
    }

    /**
     * Check if an admin is the current user.
     * @param admin Administrator to compare
     */
    public isCurrentUser$(admin: IOcbEnvironmentAdministrator): Observable<boolean> {
        return this.userService.user.pipe(
            take(1),
            map(user => user.user.id === admin.user.id),
        );
    }

    /**
     * Load data
     */
    private loadData(): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.adminService.getEnvironmentAdministrators(this.environmentId)),
                finalize(() => this.adminsGrid.instance.endCustomLoading()),
            )
            .subscribe(
                res => {
                    this.admins = res;
                },
                err => this.handleError(err),
            );
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.adminsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            }
            else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            }
        }
        else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
